import { useEffect, useState, useRef } from 'react';
import { FileText, Loader2, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';

export function ApiDocsPage() {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const userId = useAuthStore((s) => s.userId);
  const isSuperAdmin = useAuthStore((s) => s.isSubOrSuperAdmin());

  const fetchPdf = () => {
    setLoading(true);
    setEmpty(false);
    let revoke = '';
    apiClient
      .get('/api/obd/api-docs/download', { responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        revoke = url;
        setPdfUrl(url);
      })
      .catch(() => {
        setPdfUrl(null);
        setEmpty(true);
      })
      .finally(() => setLoading(false));

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  };

  useEffect(fetchPdf, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('userId', String(userId));
      await apiClient.post('/api/obd/api-docs/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documentation updated successfully');
      fetchPdf();
    } catch {
      toast.error('Failed to upload documentation');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = 'API_Documentation.pdf';
    a.click();
  };

  return (
    <FillPage>
      <PageHeader
        title="API Docs"
        description="API reference documentation for integration and development."
        actions={
          <div className="flex gap-2">
            {pdfUrl && (
              <Button variant="outline" onClick={handleDownload}>
                <Download className="h-4 w-4" />
                Download
              </Button>
            )}
            {isSuperAdmin && (
              <>
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {pdfUrl ? 'Replace' : 'Upload'} Documentation
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleUpload}
                />
              </>
            )}
          </div>
        }
      />

      {loading && (
        <div className="flex h-96 items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading documentation…
        </div>
      )}

      {empty && !loading && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <FileText className="h-10 w-10" />
            <p>No documentation uploaded yet.</p>
            {isSuperAdmin ? (
              <p className="text-sm">Click "Upload Documentation" to add your API reference PDF.</p>
            ) : (
              <p className="text-sm">Contact your administrator to upload API documentation.</p>
            )}
          </CardContent>
        </Card>
      )}

      {pdfUrl && !loading && (
        <div className="flex-1 overflow-hidden rounded-lg border border-border">
          <iframe
            src={pdfUrl}
            title="API Documentation"
            className="h-full w-full"
            style={{ minHeight: 'calc(100vh - 180px)' }}
          />
        </div>
      )}
    </FillPage>
  );
}